import type { Location, Salon, Service, Staff } from '@salonomy/types';

/**
 * WS6 brief item 7 — the onboarding wizard's go-live checklist, expressed as PURE logic (like
 * features/diary/layout.ts and calendar.ts) so it is unit-tested directly, not through React. The
 * wizard reads REAL salon state — not local wizard progress — to decide what is still missing, so a
 * salon that was provisioned through Settings (or the API) shows as already-done, and the WS3 funnel
 * can land a fresh signup straight on this checklist and see an honest "what's left".
 *
 * Each step maps 1:1 to a wizard step id; the order here is the wizard order (basics → book mode →
 * locations → team → services → hours → queue slug). "Go live" = every REQUIRED step complete.
 *
 * Note the deliberate split between the wizard's step set and the go-live requirements: `basics` and
 * `bookMode` are wizard steps but are always satisfiable from the salon row (a salon always has a
 * name + book mode the moment it exists), so they are not gating requirements — they are setup
 * choices the operator confirms. The five gating facts are the brief's literal list: ≥1 location,
 * ≥1 staff, ≥1 service, hours set, queue slug set.
 */

export type StepId =
  | 'basics'
  | 'bookMode'
  | 'locations'
  | 'team'
  | 'services'
  | 'hours'
  | 'queueSlug';

/** The ordered wizard steps. The checklist renders the gating subset; the wizard walks all of them. */
export const STEP_ORDER: StepId[] = [
  'basics',
  'bookMode',
  'locations',
  'team',
  'services',
  'hours',
  'queueSlug',
];

/** A slug that is just a placeholder ("…") or blank doesn't count as set. Mirror of the API's Slug. */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export interface GoLiveInput {
  salon: Salon | undefined;
  staff: Staff[] | undefined;
  services: Service[] | undefined;
  locations: Location[] | undefined;
  /**
   * Whether at least one bookable staff member has a weekly working-hours grid. Working hours live
   * behind a per-staff endpoint, so the wizard resolves this via data/onboarding.ts (useQueries over
   * the chairs) and hands the boolean in — keeping this module free of network concerns.
   */
  anyWorkingHours: boolean;
}

export interface StepStatus {
  id: StepId;
  /** Whether this step's underlying salon state is satisfied. */
  done: boolean;
  /** Whether completing this step is required to go live (basics/bookMode are not gating). */
  required: boolean;
}

/** Bookable chairs — the only staff for whom working hours are meaningful (mirror of chairStaff). */
const CHAIR_ROLES = new Set(['owner', 'stylist', 'chair_renter']);
export function hasBookableStaff(staff: Staff[] | undefined): boolean {
  return (staff ?? []).some((s) => s.active && CHAIR_ROLES.has(s.role));
}

export function hasSluggableQueue(salon: Salon | undefined): boolean {
  const slug = salon?.slug?.trim() ?? '';
  return slug.length > 0 && SLUG_RE.test(slug);
}

/** Compute each step's done/required status from real salon state. Stable order = STEP_ORDER. */
export function computeSteps(input: GoLiveInput): StepStatus[] {
  const { salon, staff, services, locations, anyWorkingHours } = input;
  const done: Record<StepId, boolean> = {
    // A salon row always carries a name + book mode; these are confirmations, not blockers.
    basics: Boolean(salon?.name?.trim()),
    bookMode: salon != null,
    locations: (locations ?? []).length > 0,
    team: hasBookableStaff(staff),
    services: (services ?? []).some((s) => s.active),
    hours: anyWorkingHours,
    queueSlug: hasSluggableQueue(salon),
  };
  const required: Record<StepId, boolean> = {
    basics: false,
    bookMode: false,
    locations: true,
    team: true,
    services: true,
    hours: true,
    queueSlug: true,
  };
  return STEP_ORDER.map((id) => ({ id, done: done[id], required: required[id] }));
}

/** The salon can go live when every REQUIRED step is done. */
export function canGoLive(steps: StepStatus[]): boolean {
  return steps.every((s) => !s.required || s.done);
}

/** Count of required steps still outstanding — drives the "3 things left" banner copy. */
export function outstandingRequired(steps: StepStatus[]): number {
  return steps.filter((s) => s.required && !s.done).length;
}

/** The first not-done step (any kind) — where "Resume setup" should jump to. Null when all done. */
export function firstIncompleteStep(steps: StepStatus[]): StepId | null {
  return steps.find((s) => !s.done)?.id ?? null;
}
